import { type ItemVariation } from "@interfaces/ProductInterfaces";
import { ref, onMounted, computed, reactive, markRaw, inject, type MaybeRef, watch } from "vue";
import { useSorted, useDebounceFn, useWebWorkerFn } from "@vueuse/core";
import { useFetch } from "@/helpers/composables/useFetch";
import { useMetaConversionAPI } from "@/helpers/composables/useMetaConversionAPI";
import moment from "moment";
import NProgress from "nprogress";
// import { route } from "ziggy-js";
import { useSearchSpringCookiesStore } from "@/store/searchSpringCookies"
import { formatPrice, formatTotalPrice } from "./usePriceFormatting";
export function useOrderingItemsGrid({
    items,
    formatters,
    source,
    prices
}: {
    items: ItemVariation[];
    formatters: Map;
    source?: string;
    prices?: MaybeRef;
}) {
    let route = inject('route');

    let { metaAddToCart } = useMetaConversionAPI();

    const itemsRef = ref<ItemVariation[]>(
        items.map((item) => ({
            ...item,
            quantity: item.quantity ?? null,
            original_quantity: item.quantity ?? null
        }))
    );

    const getCurrentPricing = (item)=>{
        const prices = [...(Array.isArray(item.pricing?.case_prices) ? item.pricing?.case_prices : item.pricing?.case_prices?.value ?? [])];
        const case_prices = useSorted(prices, (a, b) => a.min_order_units - b.min_order_units);

        let current_unit_price = { price: 0, uom: "N/A" };
        let quantity = +item.quantity;

        if (Number.isNaN(quantity) || quantity < 1) {
            quantity = 1;
        }

        const totalQuantity = quantity + (item.listItem?.quantity ?? 0);

        if (case_prices.value.length > 1) {
    
            for (let i = 0; i < case_prices.value.length; i++) {
                if (case_prices.value[i].min_order_units <= totalQuantity ) {
                    current_unit_price = case_prices.value[i];
                    current_unit_price.price = current_unit_price.price_to_use_with_discount;
                    current_unit_price.uom = current_unit_price?.sku_uom?.code ?? "N/A";
                } else {
                    break;
                }
            }
        } else {
            current_unit_price = {
                min_order_units: null,
                price: item.pricing?.price_to_use_with_discount,
                uom: item.pricing?.price_from?.uom,
                price_source: item.pricing?.price_from?.price_source
            };
        }

        return {
            wholesale_price: item.pricing?.original_price,
            next_case: getNextOrLastCasePrice(case_prices, totalQuantity),            
            current_unit_price: current_unit_price,
            has_case_price: case_prices.value.length > 0,
            uom: current_unit_price?.uom,
        };
    }

    const getNextOrLastCasePrice = (case_prices, itemQuantity)=>{
        const casePrice =  case_prices.value.find(
            (casePrice) => casePrice.min_order_units > itemQuantity
        ) ?? case_prices.value.filter((casePrice) => casePrice.min_order_units > 1).at(-1) ?? { min_order_units: "N/A", price_to_use_with_discount: 0 }

        return {
            ...casePrice,
            price: formatPrice(casePrice.price_to_use_with_discount),
        };
    }

    const reactiveGridItems = reactive(itemsRef.value.map((item) => {

        const formattedValues = {};
        item = {
            ...item,
            item_no: item.rothco_item_no ?? item.rothcoItem.rothco_item_no,
            item_name: item.item?.item_name ?? item.item_name,
            quantity: item.quantity,
            map: item.rothco_item?.map ?? item.map,
            msrp: item.rothco_item?.msrp ?? item.msrp,
            ...getCurrentPricing(item),
            eta_date: item.eta_date
                ? moment(item.eta_date)
                        .add(3, "days")
                        .format("MM/DD/YYYY")
                : null,
            source: source
        };
        formatters.forEach((value, key) => {
            if (
                formatters.has(key) &&
                formatters.get(key) instanceof Function
            ) {
                formattedValues[key] = formatters.get(key)(item);
            }
        });

        return {
            data: {
                ...item,
                ...formattedValues
            }
        }
    }   
));
    const gridItems = markRaw(itemsRef.value.map((item) => {

            const formattedValues = {};
            item = {
                ...item,
                item_no: item.rothco_item_no ?? item.rothcoItem.rothco_item_no,
                item_name: item.item?.item_name ?? item.item_name,
                quantity: item.quantity,
                map: item.rothco_item?.map ?? item.map,
                msrp: item.rothco_item?.msrp ?? item.msrp,
                ...getCurrentPricing(item),
                eta_date: item.eta_date
                    ? moment(item.eta_date)
                            .add(3, "days")
                            .format("MM/DD/YYYY")
                    : null,
                source: source
            };
            formatters.forEach((value, key) => {
                if (
                    formatters.has(key) &&
                    formatters.get(key) instanceof Function
                ) {
                    formattedValues[key] = formatters.get(key)(item);
                }
            });

            return reactive({
                data: {
                    ...item,
                    ...formattedValues
                }
            })
        }   
    ));

    const recalculatePricing = (index)=>{
        const pricingCheck = getCurrentPricing(gridItems[index].data);
        if(gridItems[index].data.current_unit_price.price != pricingCheck.current_unit_price.price){
            gridItems[index].data = {
                ...gridItems[index].data,
                ...pricingCheck
            }
        }
    };

    let stickyBarKey = ref(0);
    const recalculateTotals = (items)=>{
        debouncedFn(items);
    }
    const debouncedFn = useDebounceFn(async (items) => {
        stickyBarKey.value++;
        workerTerminate();
        workerFn(JSON.stringify(items)).then(response => {
            gridItemsWithQuantity.value = response;
        });
    }, 500)

    const hasCasePricing = computed(() =>{
        if(prices?.value){
            return prices.value.some((price) => price.case_prices?.filter(price => price.min_order_units > 1).length > 0)
        }
        return itemsRef.value.some((item) => item.pricing?.case_prices?.filter(price => price.min_order_units > 1).length > 0)
    });
    function getItemsWithQuantity(stringifiedGridItems) {
        const items = JSON.parse(stringifiedGridItems);
        return items.filter((item) => item.data.quantity > 0);
    }

    const { workerFn, workerStatus, workerTerminate } = useWebWorkerFn(getItemsWithQuantity);
    let gridItemsWithQuantity = ref([]);

    const totalPrice = computed(() => {
        let arr = gridItemsWithQuantity.value

        return `${arr
            .reduce((a, b) => {
                let cost = 0;
                if (b.data.current_unit_price.price && b.data.quantity) {
                    cost = +formatTotalPrice(b.data.current_unit_price.price, b.data.quantity)
                }
                return a + cost;
            }, 0).toFixed(2)}`;
    });

    const totalQuantity = computed(() => {
        let arr = gridItemsWithQuantity.value;
        return arr.reduce((a, b) => {
            const quantity = ![null, undefined, ""].includes(b.data.quantity)
                ? b.data.quantity
                : 0;
            return a + parseFloat(quantity);
        }, 0);
    });

    const totalSkusToAdd = computed(() => {
        let arr = gridItemsWithQuantity.value;
        let linesMap = new Map();
        return arr.reduce((a, b) => {
            let validQuantity = validateQuantity(b.data.quantity);
            if(validQuantity){
                if(!a.has(b.data.item_variation_id)){
                    a.set(b.data.item_variation_id, 1);
                }else{
                    a.set(b.data.item_variation_id, a.get(b.data.item_variation_id) + 1);
                }
            }
            return a;
        }, linesMap).size;
    });

    const totalStylesToAdd = computed(() => {
        let arr = gridItemsWithQuantity.value;
        let linesMap = new Map();
        return arr.reduce((a, b) => {
            let validQuantity = validateQuantity(b.data.quantity);
            if(validQuantity){
                if(!a.has(b.data.item_no)){
                    a.set(b.data.item_no, 1);
                }else{
                    a.set(b.data.item_no, a.get(b.data.item_no) + 1);
                }
            }
            return a;
        }, linesMap).size;
    });

    function validateQuantity(qty) {
        return !Number.isNaN(+qty) && +qty >= 0 && +qty % 1 == 0;
    }

    const { addCartProducts } = useSearchSpringCookiesStore();

    const addToList = useDebounceFn(function(listId, { afterFetch }) {
        const items = gridItemsWithQuantity.value
            .map((item) => ({
                source: item.data.source,
                price: item.data.current_unit_price.price,
                quantity: item.data.quantity,
                item_variation_id: item.data.item_variation_id,
                list_id: listId,
                item_no: item.data.item_no,
            }));

        if (items.length == 0 || items.some((item) => !validateQuantity(item.quantity)))
            return false;

        let itemNumbers = [...new Set(items.map((item) => item.item_no))];
        const { data } = useFetch(route("endpoint.list_item.storeBulk"), {
            beforeFetch: () => {
                NProgress.start();
            },
            afterFetch: async (ctx) => {
                addCartProducts(itemNumbers);

                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }

                if (NProgress.isStarted()) {
                    NProgress.done();
                }

                const list_item_ids = ctx.data?.map(item => item.list_item.id);
                metaAddToCart(list_item_ids);
            }
        })
            .post({
                list_item: items
            })
            .json();
        return data;
    }, 250);

    function updateListItem(list_item, { afterFetch }) {
        if (!validateQuantity(list_item.quantity)) return false;
        const { data } = useFetch(
            route("endpoint.list_item.update", [list_item.list_item_id]),
            {
                beforeFetch: () => {
                    NProgress.start();
                },
                afterFetch: async (ctx) => {
                    if (afterFetch && typeof afterFetch === "function") {
                        await afterFetch(ctx, itemsRef);
                    }
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                }
            }
        ).put({
            list_item
        });
    }

    function getUpcPricing(upcs, { afterFetch }) {
        const { data } = useFetch(route("index.quickOrdering.upcPricing"),
            {
                afterFetch: async (ctx) => {
                    let responseData = JSON.parse(ctx.data);
                    await gridItems.forEach((item, index) => {
                        if(item.data?.current_unit_price?.price === undefined) {   
                            if (responseData.upcPrices && responseData.upcPrices[item.data.upc]) {                    
                                gridItems[index].data = {
                                        ...gridItems[index].data,
                                        ...responseData.upcPrices[item.data.upc],
                                        ...getCurrentPricing(responseData.upcPrices[item.data.upc])
                                }
                            }
                        }
                    });
                    
                    if (afterFetch && typeof afterFetch === "function") {
                        await afterFetch(ctx, itemsRef);
                    }
                }
            }
        ).post({
            upcs
        });
    }

    onMounted(()=>{
        recalculateTotals(gridItems);
    })
    

    if(prices){
        watch(prices, (newPrices, oldPrices)=>{
          for(let i = 0; i < gridItems.length; i++){
            let newPrice = newPrices.value.find((price) => price.web_variation_id == gridItems[i].data.item_variation_id);
            if(newPrice){
                newPrice = JSON.parse(JSON.stringify(newPrice));
            }
            let pricing = newPrice ?? { case_prices: [] };
            
            if(pricing.case_prices.length instanceof Object){
                pricing.case_prices = Object.values(pricing.case_prices);
            }
            gridItems[i].data.pricing = pricing;

            itemsRef.value[i].pricing = pricing;
          }
        })
    }

   function clearGridItemsQuantity(){
        for(let i = 0; i < gridItems.length; i++){
            gridItems[i].data.quantity = null;
            itemsRef.value[i].quantity = null;
            reactiveGridItems[i].data.quantity = null;
        }
        gridItemsWithQuantity.value = [];
   }
    return {
        gridItems,
        recalculatePricing,
        recalculateTotals,
        hasCasePricing,
        totalPrice,
        totalQuantity,
        totalSkusToAdd,
        totalStylesToAdd,
        validateQuantity,
        addToList,
        updateListItem,
        reactiveGridItems,
        gridItemsWithQuantity,
        stickyBarKey,
        getUpcPricing,
        clearGridItemsQuantity
    };
}
